import { defineQuiz } from "./_schema";
import type { Answers, PersonaKey, Question } from "./_schema";
import { deriveWeightlossProfile } from "./quiz-weightloss-profile";
import { MM_0102 } from "./_experiments";

/**
 * WEIGHT-LOSS QUIZ -- cold-traffic destination for the hormonal-fat-storage
 * ad set ("Is hormonal fat storage quietly sabotaging your weight loss?").
 *
 * The quiz is the ad unpacked: each panel = one question, in the ad's own
 * order, so cold traffic lands on the ad it clicked. Persuasion spine is the
 * two operator-authored bridges: absolution mid-flow ("it's not your
 * willpower") -> named-villain payoff after the last question (the T-down /
 * estrogen-up fat-storage loop). The verdict is the locked T-suppression
 * surface with `visceral-adiposity` forced Primary (see
 * quizzes/quiz-weightloss-profile.ts) -- its explanation copy IS the ad's
 * mechanism ("converting testosterone into estrogen via the aromatase pathway,
 * creating a self-reinforcing loop").
 *
 * 10 single-choice screens + 3 interstitials (2 bridges + analyzing) = 13.
 * Design spec: docs/specs/2026-07-07-quiz-weightloss-quiz-design.md.
 *
 * Like quiz-v6 and cortisol, this quiz has no GLP-1 screener, so `glp1-user`
 * is not reachable from the classifier.
 */

const QUESTION_ORDER = [
  "age",
  "fat-location",
  "transformation-goal",
  "chest-change",
  "belly-diet",
  "info-absolution",
  "body-composition",
  "energy-3pm",
  "drive-drop",
  "weight-look",
  "when-started",
  "info-mechanism",
  "info-analyzing",
] as const;

const questions: Question[] = [
  {
    id: "age",
    type: "single",
    prompt: "How old are you?",
    // Reused quiz-v6 id -- option values are the deriver contract.
    options: [
      { value: "50-plus", label: "50+ years old" },
      { value: "40-49", label: "40-49 years old" },
      { value: "30-39", label: "30-39 years old" },
      { value: "20-29", label: "20-29 years old" },
    ],
  },
  {
    id: "fat-location",
    type: "single",
    prompt: "Where does your body hold fat the most?",
    options: [
      { value: "belly", label: "Belly and lower stomach" },
      { value: "chest", label: "Chest, it's gotten soft or puffy" },
      { value: "love-handles", label: "Love handles and lower back" },
      { value: "everywhere", label: "Everywhere, it's spread out" },
    ],
  },
  {
    id: "transformation-goal",
    type: "single",
    // Positive beat -- the flow's one aspiration screen before the negative run.
    prompt: "When you finally get the fat off, what do you most want to see?",
    options: [
      { value: "flat-hard", label: "A flat stomach and a hard chest again" },
      { value: "drive-confidence", label: "My drive and confidence come back" },
      { value: "full-transformation", label: "All of it, I want the full transformation" },
    ],
  },
  {
    id: "chest-change",
    type: "single",
    prompt: "Have you noticed your chest getting softer, puffier, or fuller than it used to be?",
    options: [
      { value: "yes-bothered", label: "Yes, it's bothered me for a while" },
      { value: "somewhat", label: "Somewhat, it's not as flat as it was" },
      { value: "not-sure", label: "Not sure" },
      { value: "no", label: "No" },
    ],
  },
  {
    id: "belly-diet",
    type: "single",
    prompt: "How does your belly respond when you diet?",
    options: [
      { value: "last-to-go", label: "It's the last place to go, if it goes at all" },
      { value: "shrinks-stalls", label: "It shrinks a little then stalls" },
      { value: "everywhere-but-belly", label: "I lose everywhere but there" },
      { value: "comes-off", label: "It comes off fine" },
    ],
  },
  {
    id: "info-absolution",
    type: "info",
    heading: "It's not your willpower, and it's not you.",
    body: [
      "You've put in the work. You've dieted, you've cut back, maybe you've trained hard. And the belly and the chest still won't cooperate.",
      "Here's what almost no one tells you: when your hormones are out of balance, your body is wired to store fat in those exact spots, and hold onto it, no matter how clean you eat.",
      "A few more questions and we'll show you exactly what's working against you.",
    ],
    // Bridge 1 -- absolution. Names only "hormones" generically; holds the
    // T/estrogen loop for Bridge 2. Image renders below the Continue CTA (rule
    // 20) as supporting proof; it is mechanism-silent by design (the
    // named-villain loop diagram is held for Bridge 2).
    image: {
      src: "/static/images/quiz-weightloss-absolution.webp",
      alt: "A four-panel cartoon of a heavyset man eating a healthy meal, frowning at himself in the bathroom mirror, straining on a gym machine, and looking down at a bathroom scale -- doing everything right and still stuck.",
    },
  },
  {
    id: "body-composition",
    type: "single",
    prompt: "How would you describe your body right now?",
    // Reused quiz-v6 id -- option values are the deriver contract.
    options: [
      { value: "lean-muscular", label: "Lean and muscular" },
      { value: "average", label: "Average build" },
      { value: "soft-some-muscle", label: "Smaller, but soft, or 'skinny-fat'" },
      { value: "overweight", label: "Still carrying more weight than I want" },
    ],
  },
  {
    id: "energy-3pm",
    type: "single",
    prompt: "Do you get hit with cravings or an energy crash in the afternoon?",
    // Reused quiz-v6 id -- option values are the deriver contract.
    options: [
      { value: "drained", label: "Every day, usually around 2-3pm" },
      { value: "afternoon-slump", label: "A few times a week" },
      { value: "slightly-tired", label: "Occasionally" },
      { value: "same-energy", label: "Rarely" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      body: "The afternoon crash is often a stress hormone spike. Cortisol pulls from the same raw materials your body needs to make testosterone, the hormone that helps you burn fat and hold muscle.",
    },
  },
  {
    id: "drive-drop",
    type: "single",
    prompt: "Has your sex drive, motivation, or energy dropped off lately?",
    options: [
      { value: "significantly", label: "Significantly" },
      { value: "somewhat", label: "Somewhat" },
      { value: "not-sure", label: "Not sure" },
      { value: "no", label: "No" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      body: "Testosterone drives libido, motivation, and how easily your body burns fat. When T runs low, drive fades and fat gets harder to lose.",
    },
  },
  {
    id: "weight-look",
    type: "single",
    prompt: "When you lose weight, how do you usually end up looking?",
    options: [
      { value: "smaller-soft", label: "Smaller but soft, never lean" },
      { value: "little-better", label: "A little better, but not defined" },
      { value: "pretty-lean", label: "Pretty lean" },
      { value: "havent-lost", label: "I haven't been able to lose much" },
    ],
  },
  {
    id: "when-started",
    type: "single",
    prompt: "How long have you been fighting this?",
    // Reused quiz-v6 id -- option values are the deriver contract.
    options: [
      { value: "over-year", label: "More than a few years" },
      { value: "past-year", label: "About a year" },
      { value: "few-months", label: "The past few months" },
      { value: "not-sure", label: "Not sure" },
    ],
  },
  {
    id: "info-mechanism",
    type: "info",
    heading: "Your hormones may be clinging to fat in a way diet can't seem to touch.",
    body: [
      "Here's the cycle most men get stuck in: as testosterone drops and estrogen creeps up, your body starts storing fat in the belly and chest, and defending it. That's the gut and the stubborn chest that survive every diet.",
      "And the cycle continues. More belly fat pushes estrogen higher, which stores more fat, which pulls testosterone lower. Dieting harder doesn't break the loop, because the loop is hormonal.",
      "Support your hormone balance and your body can finally let the fat go. Let's see how much this cycle is working against you.",
    ],
    // Bridge 2 -- named-villain payoff. Reveals the T-down / estrogen-up storage
    // loop = the visceral-adiposity forced cause. Image renders below the
    // Continue CTA (rule 20): the self-reinforcing loop diagram (deliberate UMS
    // reveal) -- LOW T at center, T-down/cortisol-up -> stored fat converts T to
    // estrogen -> belly fat climbs -> back to low T.
    image: {
      src: "/static/images/quiz-weightloss-mechanism.webp",
      alt: "A circular diagram of a self-reinforcing hormonal loop centered on LOW T: testosterone drops as cortisol rises, stored fat converts testosterone into estrogen, and climbing belly fat feeds back into lower testosterone.",
    },
  },
  {
    id: "info-analyzing",
    type: "info",
    // Pays off Bridge 2's closing line ("Let's see how much this cycle is
    // working against you").
    heading: "Analyzing your hormonal fat-storage profile…",
    body: [
      "Counting the signs you just reported, and measuring how hard your testosterone balance could be working against your weight loss.",
    ],
    animation: "analyzing",
    autoAdvanceMs: 5000,
  },
];

export const quizWeightlossQuiz = defineQuiz({
  id: "quiz-weightloss",
  slug: "quiz-weightloss",
  experiment: MM_0102,
  meta: {
    title: "What's Really Driving Your Stubborn Fat",
    introHeadline: "Is hormonal fat storage quietly sabotaging your weight loss?",
    introAccent: "sabotaging",
    introBody:
      "See why the soft belly and stubborn chest fat won't budge, no matter how hard you diet, in this 2 minute quiz.",
    testimonial: {
      // Real verified weight-loss review (operator-confirmed). Angle-matched
      // (scale/weight register), reveal-safe (names no product or T category),
      // says "quiz" not bare "it". Default avatar placeholder (v1).
      quote:
        "Cut calories hard for months and the scale wouldn't move. This quiz finally explained why in about two minutes. So glad I took it.",
      author: "DAVID R., Verified Review",
    },
    // MM-0102 -- see quizzes/_experiments.ts.
    introVariantTag: "control",
  },
  questions,

  branching(_answers: Answers, currentQuestionId: string): string | null {
    const idx = QUESTION_ORDER.indexOf(currentQuestionId as (typeof QUESTION_ORDER)[number]);
    if (idx < 0) return null;
    const next = QUESTION_ORDER[idx + 1];
    return next ?? null;
  },

  classifier(answers: Answers): PersonaKey {
    const age = answers["age"];
    const body = answers["body-composition"];
    const energy = answers["energy-3pm"];
    const onset = answers["when-started"];
    const drive = answers["drive-drop"];
    const goal = answers["transformation-goal"];

    // Note: fat-location, chest-change, belly-diet, and weight-look are not read
    // here -- they are persuasion / severity screens that feed the deriver via
    // quizzes/quiz-weightloss-profile.ts. The classifier routes on age,
    // body-composition, energy, onset, drive, and the aspiration goal only.

    // Performance-focused: lean body chasing definition / drive back. Runs
    // first -- lean-muscular users never reach trt-alternative even with a
    // long-running collapse. Body composition signals audience.
    if (body === "lean-muscular" && (goal === "drive-confidence" || energy === "drained")) {
      return "performance";
    }

    // TRT-alternative: long-running collapse across energy + drive.
    if (onset === "over-year" && energy === "drained" && (drive === "significantly" || age === "50-plus")) {
      return "trt-alternative";
    }

    // Skeptical buyer: can't place when it started.
    if (onset === "not-sure") {
      return "skeptical-buyer";
    }

    // Aging men: 40+ with visible softening.
    if ((age === "50-plus" || age === "40-49") && (body === "soft-some-muscle" || body === "overweight")) {
      return "aging-men";
    }

    return "everyday-man";
  },

  deriveProfile: deriveWeightlossProfile,
});
